/**
 * Confirmation dialog when the user clicks 👍 on a chat answer.
 *
 * R-143-CHAT-ASSISTANT Phase D.
 *
 * Shows the question + answer preview + two CTAs:
 *   - "Save to my reports" (private)
 *   - "Save + share with team" (gated to users with a company)
 */
import React, { useCallback, useState } from 'react';
import { CheckCircle2, Share2, X, Loader2, Database } from 'lucide-react';

import { Button } from '../ui/button';

interface Props {
  isOpen: boolean;
  question: string;
  answerPreview: string;
  canShareWithTeam: boolean;
  onClose: () => void;
  onConfirm: (shareWithTeam: boolean) => Promise<void>;
}

export function SaveToModelDialog({
  isOpen,
  question,
  answerPreview,
  canShareWithTeam,
  onClose,
  onConfirm,
}: Props): React.JSX.Element | null {
  const [busy, setBusy] = useState(false);

  const handle = useCallback(async (shareWithTeam: boolean) => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm(shareWithTeam);
      onClose();
    } finally {
      setBusy(false);
    }
  }, [busy, onConfirm, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4 font-mono"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-to-model-title"
      data-testid="save-to-model-dialog"
    >
      <div className="bg-background border-2 border-border max-w-lg w-full p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" aria-hidden="true" />
            <h2
              id="save-to-model-title"
              className="text-sm font-bold uppercase tracking-widest text-text-primary"
            >
              Save to Datacrate model
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-muted hover:text-text-primary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-[10px] text-text-secondary uppercase tracking-widest leading-relaxed">
          Save this answer so it can be used as evidence when Datacrate
          generates reports. The original conversation stays private.
        </p>

        <section className="space-y-2">
          <div className="text-[9px] font-bold uppercase tracking-widest text-text-muted">
            Question
          </div>
          <div className="border border-border-subtle p-3 text-sm text-text-primary line-clamp-2">
            {question}
          </div>
        </section>

        <section className="space-y-2">
          <div className="text-[9px] font-bold uppercase tracking-widest text-text-muted">
            Answer (preview)
          </div>
          <div className="border border-border-subtle p-3 text-sm text-text-primary max-h-40 overflow-y-auto whitespace-pre-wrap">
            {answerPreview.length > 800 ? `${answerPreview.slice(0, 800)}…` : answerPreview}
          </div>
        </section>

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            disabled={busy}
            onClick={() => void handle(false)}
            className="h-9 rounded-md uppercase tracking-widest text-[10px] font-bold"
            data-testid="save-to-model-primary"
          >
            {busy
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <><CheckCircle2 className="w-4 h-4 mr-2" /> Save to my reports</>}
          </Button>
          {canShareWithTeam && (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => void handle(true)}
              className="h-9 rounded-md uppercase tracking-widest text-[10px] font-bold"
              data-testid="save-to-model-secondary"
            >
              <Share2 className="w-4 h-4 mr-2" /> Save + share with team
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
